import React, { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { currentRef, settledCapture } from "../lib/affiliateRef";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import usePlatformConfig from "@/lib/usePlatformConfig";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { SignupForm } from "./signup/SignupForm";
import { SignupSummary } from "./signup/SignupSummary";
// Relative, not "@/...": real module under Jest, no alias there. Only `validateWith`
// (the flattener) is reused here, not the shared emailField/requiredField helpers —
// this schema's messages have to be "saas" namespace keys (matching this page's own
// `t`), and lib/validation.js's exported fields hardcode "common" namespace keys.
import { validateWith } from "../lib/validation";
import { previewCode } from "../platformOffers/api";
import { platformOfferErrorMessage } from "../platformOffers/errors";
import { OPERATING_CURRENCIES, isOperatingCurrency } from "../lib/currencies";

/**
 * Presence + minimum-length checks for the three required fields SignupForm.jsx (a
 * peer-claimed, presentational-only component — no logic of its own) renders. `slug`
 * chains TWO checks so a genuinely blank slug still reads "fill in the required
 * fields" (first issue wins) and only a too-short-but-typed slug gets the more precise
 * message — the exact distinction the manual `if` chain this replaces used to draw.
 */
const signupFormSchema = z.object({
  email: z.string().trim().min(1, "signup.toast.fillRequired"),
  // OPTIONAL. Blank does not block the purchase: the backend derives the practice's name
  // from the email the account is opened under, and it is editable from Settings
  // afterwards. Requiring it here is what produced practices named after their own slug.
  // Still bounded, and still ≥2 when something IS typed, so the two halves of the rule
  // cannot disagree with `tenant_identity.validated_practice_name`.
  practice_name: z.string().trim()
    .max(120, "signup.toast.practiceNameTooLong")
    .refine((v) => v === "" || v.length >= 2, "signup.toast.practiceNameTooShort"),
  slug: z.string().trim()
    .min(1, "signup.toast.fillRequired")
    .min(2, "signup.toast.slugTooShort"),
});

// A `?currency=` a visitor can type is UNTRUSTED: anything outside the operating set (a
// retired "eur" link, a typo) used to travel to `/saas/signup` and come back as an opaque
// 422 on a form the visitor cannot correct, because the field is not rendered.
const operatingCurrency = (value) =>
  isOperatingCurrency(value) ? String(value).trim().toLowerCase() : OPERATING_CURRENCIES[0];

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const t = useT("saas");
  const tc = useT("common");
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [plansFailed, setPlansFailed] = useState(false);
  const [form, setForm] = useState({
    email: "",
    owner_name: "",
    practice_name: "",
    slug: "",
    plan_id: params.get("plan") || "seed",
    currency: operatingCurrency(params.get("currency")),
    billing_cycle: params.get("billing") === "annual" ? "annual" : "monthly",
  });
  const [submitting, setSubmitting] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // The applied platform codes, one §5.1 preview per slot. Sent as `promo_code` /
  // `gift_card_code`; the server re-decides both at submit, so a preview is a promise of
  // what the code does, not a reservation.
  const [offers, setOffers] = useState({ promo: null, giftcard: null });
  // `/platform/signup?code=GC-...` (the gift card email's link, contract §7) pre-fills the field.
  // Consumed by the first applied code, so removing it later does not re-fill it.
  const [prefillCode, setPrefillCode] = useState(() => params.get("code") || "");
  // The trial length and Stripe's demo flag are now a SHARED, module-cached read
  // (`usePlatformConfig`) instead of a fetch this page owned alone: the landing page and
  // the success screen quote the same `{days}` this form does, and a literal "30" written
  // three times could no longer be kept in sync with what Stripe was actually told.
  const config = usePlatformConfig();

  // The plan list is the one thing the form cannot be submitted without, so a failed read
  // is SHOWN (with a retry) rather than left as an unhandled rejection and an empty picker.
  const loadPlans = useCallback(() => {
    setPlansFailed(false);
    return api.get("/saas/plans")
      .then((r) => setPlans(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPlansFailed(true));
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  useEffect(() => {
    if (params.get("cancelled") === "1") {
      toast.warning(t("signup.toast.paymentCancelled"));
    }
  }, [params, t]);

  // Pré-remplit l'email/nom si l'utilisateur s'est authentifié via Google
  useEffect(() => {
    if (user?.email) {
      // L'e-mail du compte Google est AUTORITAIRE : c'est lui qui servira à la
      // connexion (/saas/my-tenant résout le tenant par owner_email). Un e-mail
      // libre divergent = compte qui ne pourra jamais ouvrir son dashboard.
      setForm((f) => ({
        ...f,
        email: user.email,
        owner_name: f.owner_name || user.name || "",
      }));
    }
  }, [user]);

  const handleSlug = (val) => {
    const cleaned = val.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 60);
    setForm((f) => ({ ...f, slug: cleaned }));
    setSlugAvailable(null);
  };

  // Vérification live de disponibilité du slug, débattue (~400ms) pour éviter
  // un appel par frappe. `latestSlugRef` protège contre les réponses tardives
  // (race) : si le slug a changé pendant l'aller-retour réseau, on ignore la réponse.
  const latestSlugRef = useRef("");
  useEffect(() => {
    const slug = form.slug;
    latestSlugRef.current = slug;
    if (!slug || slug.length < 2) {
      setSlugAvailable(null);
      return;
    }
    const timer = setTimeout(() => {
      api
        .get("/saas/check-slug", { params: { slug } })
        .then((r) => {
          if (latestSlugRef.current === slug) {
            setSlugAvailable(r.data);
          }
        })
        .catch(() => {
          if (latestSlugRef.current === slug) {
            setSlugAvailable(null);
          }
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [form.slug]);

  const applyOffer = (preview) => {
    setOffers((o) => ({ ...o, [preview.kind]: preview }));
    setPrefillCode("");
  };
  const removeOffer = (kind) => setOffers((o) => ({ ...o, [kind]: null }));

  // A preview is priced against one plan, currency and cycle. When any of the three
  // changes, every applied code is previewed again: its amounts are stale, and it may no
  // longer apply at all (a plan-restricted promo, a gift card in the other currency). A
  // code that no longer applies is removed and the visitor is told why, rather than
  // being sent to a signup the server will refuse.
  const { plan_id: planId, currency, billing_cycle: billingCycle } = form;
  const pricedAt = useRef(`${planId}|${currency}|${billingCycle}`);
  const offersRef = useRef(offers);
  offersRef.current = offers;
  useEffect(() => {
    const key = `${planId}|${currency}|${billingCycle}`;
    if (pricedAt.current === key) return;
    pricedAt.current = key;
    const applied = Object.values(offersRef.current).filter(Boolean);
    if (!applied.length) return;
    const params = { plan_id: planId, currency, billing_cycle: billingCycle };
    Promise.all(applied.map((o) => previewCode({ code: o.code, ...params })
      .then((preview) => ({ was: o, preview }), (error) => ({ was: o, error }))))
      .then((results) => {
        // Superseded by a later change of plan, currency or cycle: that run decides.
        if (pricedAt.current !== key) return;
        setOffers((prev) => {
          const next = { ...prev };
          for (const { was, preview } of results) {
            // Removed (or replaced) while the request was in flight: not ours to touch.
            if (prev[was.kind]?.code !== was.code) continue;
            next[was.kind] = preview && preview.kind === was.kind ? preview : null;
          }
          return next;
        });
        results.filter((r) => r.error).forEach((r) => {
          toast.error(t("signup.code.removedAfterChange", {
            code: r.was.code,
            reason: platformOfferErrorMessage(r.error, tc, tc("platformOffers.error.generic")),
          }));
        });
      });
  }, [planId, currency, billingCycle, t, tc]);

  // GIS pose la session serveur puis rafraîchit le AuthContext ; l'effet ci-dessus
  // (sur `user`) pré-remplit alors email/nom. Pas de navigation : on reste sur /platform/signup.
  const handleGoogleSignedIn = () => {};

  const submit = async (e) => {
    e.preventDefault();
    // Converged onto the shared recipe (lib/validation.js): one schema run through
    // validateWith() instead of three hand-rolled `if`s, first-issue-per-field wins.
    // SignupForm.jsx renders no per-field error slot (peer-claimed, presentational
    // only), so — unlike the recipe's usual inline-FieldError half — this still
    // surfaces as ONE toast for the first offending field, exactly as the code it
    // replaces did.
    const errors = validateWith(signupFormSchema, form);
    if (errors) {
      toast.error(t(Object.values(errors)[0]));
      return;
    }
    if (!acceptedTerms) {
      toast.error(t("signup.toast.acceptTermsRequired"));
      return;
    }

    setSubmitting(true);
    try {
      const origin = window.location.origin;
      // Attribution. The capture belongs to <AffiliateRefCapture />, mounted once per
      // AppRouter branch: it asks the server for the referral ticket. A visitor can submit
      // this form before that answer has landed, so wait for it — a bare code read from
      // the URL is not attributable any more, only the server-issued ticket is.
      await settledCapture();
      const ref = currentRef();
      const { data } = await api.post("/saas/signup", {
        ...form,
        accepted_terms: true,
        locale: lang,
        origin_url: origin,
        ref,
        promo_code: offers.promo?.code || "",
        gift_card_code: offers.giftcard?.code || "",
      });
      if (data.demo_mode) {
        toast.success(t("signup.toast.demoCreated"));
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      // A code refusal (PLATFORM_CODE_INVALID / _RACE, contract §5.2) is worded by the
      // platform-offer map; anything else falls through to the generic signup error.
      toast.error(platformOfferErrorMessage(err, tc, t("signup.toast.genericError")));
      setSubmitting(false);
    }
  };

  const selectedPlan = plans.find((p) => p.plan_id === form.plan_id);

  return (
    <div className="min-h-screen bg-sol-midnight text-white">
      <nav className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/platform" className="flex items-center gap-2.5 text-white/80 hover:text-white" data-testid="back-to-platform">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-sol font-semibold text-lg">Solunary</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="dark" />
            <Link to="/demo" className="text-sm text-white/40 hover:text-white/70">Demo</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-5 gap-12">
        <SignupForm
          form={form}
          setForm={setForm}
          handleSlug={handleSlug}
          slugAvailable={slugAvailable}
          submit={submit}
          submitting={submitting}
          acceptedTerms={acceptedTerms}
          setAcceptedTerms={setAcceptedTerms}
          config={config}
          plans={plans}
          plansFailed={plansFailed}
          onRetryPlans={loadPlans}
          onGoogleSignedIn={handleGoogleSignedIn}
          user={user}
          t={t}
          tc={tc}
          offers={offers}
          onOfferApplied={applyOffer}
          onOfferRemoved={removeOffer}
          initialCode={prefillCode}
        />
        <SignupSummary selectedPlan={selectedPlan} form={form} offers={offers} trialDays={config.trialPeriodDays} />
      </div>
    </div>
  );
}
